var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useMemo } from 'react';
import { StsuiDashboard, NotificationComp, convertToKAndM } from 'ui-kit';
import { getIcon } from '../utils/getIcon';
import { useStsuiSubscription } from '../hooks/useStsuiSubscription';
import { useSubscription } from '../hooks/useSubscription';
import { useSignAndExecuteTransaction } from '../hooks/walletCompat';
import { useBalances } from '../context/BalanceContext';
import { useNetworkString } from '../context/NetworkContext';
import { stsuiTransactionHandler } from '../utils/stsuiTransactionHandler';
import BigNumber from 'bignumber.js';
import { SUI_COIN_TYPE, SUI_COIN_TYPE_PADDED, STSUI_COIN_TYPE, SUI_DECIMALS, calculateReceiveAmount, } from '../utils/stsuiConstants';
import { stSuiFaqSection } from '../utils/stSuiFaq';
import { IS_WIND_DOWN_ACTIVE } from '../utils/windDownConfig';
const StsuiPage = ({ themeName, address, walletConnectFunctionality, }) => {
    var _a, _b, _c;
    const { data } = useStsuiSubscription('room-staking-page');
    const { data: coinInfoData } = useSubscription('coin_info');
    const signAndExecuteTransaction = useSignAndExecuteTransaction();
    const { walletBalancesMap } = useBalances();
    const network = useNetworkString();
    const [isTransactionLoading, setIsTransactionLoading] = useState(false);
    const [notificationInfo, setNotificationInfo] = useState({
        title: '',
        description: '',
        image: null,
        spinner: null,
    });
    const [isNotificationOpen, setIsNotificationOpen] = useState(false);
    const [resetInput, setResetInput] = useState(false);
    const suiBalance = useMemo(() => {
        if (!address || !walletBalancesMap)
            return '0';
        const balance = walletBalancesMap.get(SUI_COIN_TYPE) || '0';
        return new BigNumber(balance).dividedBy(new BigNumber(10).pow(SUI_DECIMALS)).toString();
    }, [address, walletBalancesMap]);
    const stSuiBalance = useMemo(() => {
        if (!address || !walletBalancesMap)
            return '0';
        const balance = walletBalancesMap.get(STSUI_COIN_TYPE) || '0';
        return new BigNumber(balance).dividedBy(new BigNumber(10).pow(SUI_DECIMALS)).toString();
    }, [address, walletBalancesMap]);
    const suiBalanceRaw = useMemo(() => {
        if (!address || !walletBalancesMap)
            return '0';
        return walletBalancesMap.get(SUI_COIN_TYPE) || '0';
    }, [address, walletBalancesMap]);
    const stakeToken = {
        symbol: 'SUI',
        icon: getIcon('sui'),
        price: ((_a = coinInfoData === null || coinInfoData === void 0 ? void 0 : coinInfoData[SUI_COIN_TYPE_PADDED]) === null || _a === void 0 ? void 0 : _a.price) || '0',
        balance: suiBalance,
    };
    const receiveToken = {
        symbol: 'stSUI',
        icon: getIcon('stsui'),
        price: ((_b = coinInfoData === null || coinInfoData === void 0 ? void 0 : coinInfoData[STSUI_COIN_TYPE]) === null || _b === void 0 ? void 0 : _b.price) || '0',
        balance: stSuiBalance,
    };
    // Wind-down mode opens the page on Instant Unstake, since staking is paused.
    const initialTab = IS_WIND_DOWN_ACTIVE ? 'Instant Unstake' : 'Stake';
    const handleSubmit = (amount, action, displayAmount) => __awaiter(void 0, void 0, void 0, function* () {
        if (!address) {
            console.error('Wallet not connected');
            return;
        }
        // Wind-down mode: never build a stake transaction. Unstaking stays open.
        if (IS_WIND_DOWN_ACTIVE && action === 'Stake') {
            setNotificationInfo({
                title: 'Staking Paused',
                description: 'AlphaFi is winding down, so new stSUI staking is paused. You can still instant unstake.',
                image: getIcon('failed'),
                spinner: false,
            });
            setIsNotificationOpen(true);
            return;
        }
        const actionType = action === 'Stake' ? 'stake' : 'unstake';
        yield stsuiTransactionHandler({
            amount,
            network: network === 'mainnet' ? 'mainnet' : undefined,
            action: actionType,
            address: address,
            signAndExecuteTransaction,
            setNotificationInfo,
            setIsNotificationOpen,
            setIsTransactionLoading,
            displayAmount,
            suiBalance: suiBalanceRaw,
            stSuiExchangeRate: data === null || data === void 0 ? void 0 : data.stSuiExchangeRate,
            unstakingFee: data === null || data === void 0 ? void 0 : data.unstakingFee,
            resetInputValues: () => {
                setResetInput((prev) => !prev);
            },
        });
    });
    return (_jsxs("div", { className: "w-full min-h-screen theme-bg-alphafi-main-body", children: [_jsx(StsuiDashboard, { stats: {
                    label1: 'stSUI Market Cap',
                    value1: '$' + (data === null || data === void 0 ? void 0 : data.stSuiMarketCap),
                    label2: 'Total SUI Staked',
                    value2: Number(data === null || data === void 0 ? void 0 : data.totalSuiStaked).toFixed(0) || '0',
                    label3: 'Total Stakers',
                    value3: (data === null || data === void 0 ? void 0 : data.totalStakers) || '0',
                    label4: 'Current Epoch',
                    value4: (data === null || data === void 0 ? void 0 : data.currentEpoch) || '0',
                    statsIcon: themeName === 'dark' ? getIcon('statsWhiteIcon') : getIcon('statsBlackIcon'),
                }, stakeToken: stakeToken, receiveToken: receiveToken, exchangeRate: parseFloat((data === null || data === void 0 ? void 0 : data.stSuiExchangeRate) || '0'), apr: convertToKAndM((data === null || data === void 0 ? void 0 : data.APY) || '0'), unstakingFee: ((_c = data === null || data === void 0 ? void 0 : data.unstakingFee) === null || _c === void 0 ? void 0 : _c.toString()) || '0', maxStakeAmount: suiBalance, maxUnstakeAmount: stSuiBalance, onSubmit: handleSubmit, isTransactionLoading: isTransactionLoading, isWalletConnected: address ? true : false, initialTab: initialTab, calculateReceiveAmount: calculateReceiveAmount, onResetInput: resetInput, walletConnectFunctionality: address ? undefined : walletConnectFunctionality, faqSection: stSuiFaqSection }), _jsx(NotificationComp, { isOpen: isNotificationOpen, setIsOpen: setIsNotificationOpen, info: notificationInfo })] }));
};
export default StsuiPage;
